import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/stores/auth';
import { canAccessPage, type RoleName } from '@/lib/utils/role-access';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PageGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * Declarative page access guard to avoid navigate-effect loops.
 */
export function PageGuard({
  children,
  fallback,
  redirectTo = '/dashboard'
}: PageGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInitialized, user, isAuthenticated, isLoading } = useAuthStore();

  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  const userRole = user?.role?.name as RoleName | undefined;
  const hasAccess = canAccessPage(location.pathname, userRole);

  if (!hasAccess) {
    if (fallback) return <>{fallback}</>;

    // Avoid ping-pong loops if redirect target is current path.
    if (location.pathname !== redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Lock className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle className="text-xl">Access Denied</CardTitle>
            <CardDescription>
              You don&apos;t have permission to access this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/dashboard', { replace: true })} className="w-full">
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

export function withPageGuard<P extends object>(
  Component: React.ComponentType<P>,
  options?: { redirectTo?: string; fallback?: React.ReactNode }
) {
  return function ProtectedPage(props: P) {
    return (
      <PageGuard {...options}>
        <Component {...props} />
      </PageGuard>
    );
  };
}
